import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  OpenAILogo, AnthropicLogo, GeminiLogo, MistralLogo,
  AzureLogo, AWSLogo, GCPLogo, HarmonicMark,
} from '../landing/EcosystemSection';

// Integration names + marks, mirrored from the landing "Open Ecosystem" section.
const INTEGRATIONS = [
  { name: 'OpenAI', node: <span className="text-slate-800"><OpenAILogo /></span> },
  { name: 'Anthropic', node: <span className="text-slate-800"><AnthropicLogo /></span> },
  { name: 'Gemini', node: <GeminiLogo /> },
  { name: 'Mistral', node: <MistralLogo /> },
  { name: 'Microsoft Azure', node: <AzureLogo /> },
  { name: 'Amazon Web Services', node: <AWSLogo /> },
  { name: 'Google Cloud', node: <GCPLogo /> },
  { name: 'Core42', node: <span className="font-mono font-black text-sm text-[#F58220]">C42</span> },
  { name: 'PitchBook', node: <img src="/logos/pitchbook-mark.png" alt="" className="h-5 w-auto object-contain" /> },
  { name: 'Harmonic', node: <HarmonicMark /> },
  { name: 'Datasite', node: <img src="/logos/datasite-mark.png" alt="" className="h-5 w-auto object-contain" /> },
  { name: 'Affinity', node: <img src="/logos/affinity-mark.png" alt="" className="h-[22px] w-auto object-contain" /> },
];

const SIZE = 700;       // ring canvas (px)
const RADIUS = 300;     // tile orbit radius
const TILE = 80;        // tile size
const CX = SIZE / 2;
const CY = SIZE / 2;
const STEP = 360 / INTEGRATIONS.length;

// Shortest angular distance to the 3 o'clock (east = 0deg) position.
const distToEast = (deg) => {
  let d = ((deg % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
};

export const EcosystemRing = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);

  // Scale the fixed-size ring down to fit the available width (mobile-friendly).
  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) setScale(Math.min(1, wrapRef.current.clientWidth / (SIZE + 24)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const onMove = useCallback((e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width; // 0..1
    setRotation((x - 0.5) * 360);
  }, []);

  // Active tile = whichever currently sits nearest the east (3 o'clock) slot.
  let active = 0;
  let best = Infinity;
  INTEGRATIONS.forEach((_, i) => {
    const d = distToEast(i * STEP + rotation);
    if (d < best) { best = d; active = i; }
  });

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative bg-[#FBFBFB] py-10 sm:py-16 overflow-x-clip select-none"
    >
      <div ref={wrapRef} className="w-full px-4 flex justify-center">
        <div style={{ width: SIZE * scale, height: SIZE * scale, position: 'relative' }}>
        <div
          className="relative"
          style={{ width: SIZE, height: SIZE, transform: `scale(${scale})`, transformOrigin: 'top left' }}
        >
          {/* Center content: Open Ecosystem. Sovereign Perimeter. */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-16 pointer-events-none">
            <span className="inline-block text-[9px] font-bold uppercase tracking-[0.15em] text-[#F58220] border border-[#F58220]/30 rounded px-2.5 py-1 mb-4 font-mono">
              THE APEX DEAL LAYER
            </span>
            <h2 className="text-[2rem] sm:text-[2.4rem] font-black text-[#0F172A] leading-[1.08] tracking-tight">
              Open Ecosystem.<br />Sovereign Perimeter.
            </h2>
            <p className="text-[13px] text-slate-500 leading-relaxed mt-4 max-w-[300px]">
              Your cloud. Your models. Your proprietary deal playbooks. Unified by Frontier.
            </p>
            <button
              onClick={() => navigate('/#sovereignty')}
              className="pointer-events-auto mt-6 inline-flex items-center gap-2 text-[13px] font-semibold text-[#0F172A] hover:text-[#F58220] transition-colors group"
            >
              View integrations
              <span className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-[#F58220]/15 flex items-center justify-center transition-colors">
                <ArrowRight size={14} />
              </span>
            </button>
          </div>

          {/* Orbiting tiles */}
          {INTEGRATIONS.map((item, i) => {
            const angle = i * STEP + rotation;
            const rad = (angle * Math.PI) / 180;
            const dx = Math.cos(rad) * RADIUS;
            const dy = Math.sin(rad) * RADIUS;
            const yf = Math.sin(rad); // +1 bottom, -1 top
            const opacity = yf <= 0 ? 1 : Math.max(0.12, 1 - yf * 0.9);
            const isActive = i === active;
            return (
              <div
                key={item.name}
                className="absolute rounded-2xl bg-white border flex items-center justify-center shadow-sm"
                style={{
                  width: TILE,
                  height: TILE,
                  left: CX,
                  top: CY,
                  transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${isActive ? 1.12 : 1})`,
                  opacity,
                  borderColor: isActive ? 'rgba(245,130,32,0.45)' : 'rgba(226,232,240,1)',
                  boxShadow: isActive ? '0 12px 30px -10px rgba(245,130,32,0.35)' : '0 2px 8px -4px rgba(15,23,42,0.15)',
                  transition: 'transform 0.25s ease-out, opacity 0.25s ease-out, box-shadow 0.25s ease-out, border-color 0.25s ease-out',
                  zIndex: isActive ? 20 : 10,
                }}
              >
                <div className="scale-[1.35]">{item.node}</div>
              </div>
            );
          })}

          {/* Active integration name, pinned at the 3 o'clock slot (desktop only — cursor-driven) */}
          <div
            className="absolute hidden lg:flex items-center"
            style={{ left: CX + RADIUS + TILE / 2 + 18, top: CY, transform: 'translateY(-50%)' }}
          >
            <span className="text-xl font-medium text-stone-900 whitespace-nowrap">{INTEGRATIONS[active].name}</span>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
};
